import { useState } from "react";

const palette = {
    bg: "#0A0E14",
    surface: "#121826",
    raised: "#182032",
    border: "#202B40",
    accent: "#6FB4D9",
    accentDim: "#0F2230",
    accentText: "#6FB4D9",
    text: "#EAEFF7",
    muted: "#7C8AA3",
    faint: "#3B4760",
    success: "#22C97A",
    successDim: "#0A2218",
    danger: "#FF5C7A",
    dangerDim: "#2A0E16",
    warning: "#FFB547",
    warningDim: "#251A06",
    info: "#8FA9FF",
    infoDim: "#131A33",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  .fo-pp {
    background: ${palette.bg};
    color: ${palette.text};
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    line-height: 1.7;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  /* faint concentric-ring backdrop, echoing the gravity clock face */
  .fo-rings-bg {
    position: fixed;
    inset: 0;
    background-image:
      repeating-radial-gradient(
        circle at 50% -10%,
        ${palette.border}26 0px,
        ${palette.border}26 1px,
        transparent 1px,
        transparent 90px
      );
    pointer-events: none;
    z-index: 0;
    opacity: 0.6;
  }

  .fo-glow {
    position: fixed;
    top: -120px;
    left: 50%;
    transform: translateX(-50%);
    width: 640px;
    height: 420px;
    background: radial-gradient(ellipse, ${palette.accent}16 0%, transparent 65%);
    pointer-events: none;
    z-index: 0;
    animation: fo-pulse 5s ease-in-out infinite alternate;
  }

  @keyframes fo-pulse {
    from { opacity: 0.55; }
    to   { opacity: 1; }
  }

  .fo-wrap {
    position: relative;
    z-index: 1;
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px 80px;
  }

  /* Hero */
  .fo-hero {
    text-align: center;
    padding: 56px 0 44px;
    animation: fo-fadeup .6s ease both;
  }

  .fo-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}44;
    border-radius: 100px;
    padding: 5px 14px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: ${palette.accent};
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .fo-hero h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(30px, 6vw, 48px);
    font-weight: 700;
    letter-spacing: -1px;
    line-height: 1.1;
    margin-bottom: 12px;
  }

  .fo-hero h1 em { color: ${palette.accent}; font-style: normal; }

  .fo-hero-sub {
    color: ${palette.muted};
    font-size: 14px;
    max-width: 440px;
    margin: 0 auto 18px;
  }

  .fo-meta {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    color: ${palette.faint};
    font-size: 12px;
  }

  .fo-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  /* Divider */
  .fo-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, ${palette.border}, transparent);
    margin: 36px 0;
  }

  /* Section */
  .fo-section {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 16px;
    padding: 24px 26px 20px;
    margin-bottom: 12px;
    transition: border-color .2s;
    animation: fo-fadeup .5s ease both;
  }

  .fo-section:hover { border-color: ${palette.accent}30; }

  .fo-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .fo-icon {
    width: 36px; height: 36px; min-width: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .fo-section h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -.1px;
  }

  .fo-section p, .fo-section li {
    color: ${palette.muted};
    font-size: 14px;
    line-height: 1.75;
  }

  .fo-section ul {
    padding-left: 18px;
    margin-top: 8px;
  }

  .fo-section li { margin-bottom: 5px; }

  .fo-section p + p { margin-top: 10px; }

  .fo-section a {
    color: ${palette.info};
    text-decoration: none;
    border-bottom: 1px solid ${palette.info}44;
    transition: border-color .15s;
  }

  .fo-section a:hover { border-color: ${palette.info}; }

  /* callout boxes */
  .fo-box {
    border-radius: 10px;
    padding: 11px 15px;
    margin-top: 14px;
    font-size: 13px;
    display: flex;
    gap: 9px;
    align-items: flex-start;
    line-height: 1.6;
  }

  .fo-box-success {
    background: ${palette.successDim};
    border: 1px solid ${palette.success}33;
    color: ${palette.success}CC;
  }

  .fo-box-warning {
    background: ${palette.warningDim};
    border: 1px solid ${palette.warning}33;
    color: ${palette.warning}CC;
  }

  .fo-box-danger {
    background: ${palette.dangerDim};
    border: 1px solid ${palette.danger}33;
    color: ${palette.danger}CC;
  }

  /* Grid cards */
  .fo-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 14px 0 4px;
  }

  @media (max-width: 520px) { .fo-grid { grid-template-columns: 1fr; } }

  .fo-card {
    background: ${palette.raised};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 13px 15px;
    display: flex;
    align-items: flex-start;
    gap: 11px;
  }

  .fo-card-icon {
    width: 32px; height: 32px; min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .fo-card strong {
    font-size: 13px;
    font-weight: 600;
    color: ${palette.text};
    display: block;
    margin-bottom: 3px;
  }

  .fo-card p {
    font-size: 12px;
    color: ${palette.muted};
    line-height: 1.5;
    margin: 0 !important;
  }

  /* Contact */
  .fo-contact {
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}33;
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    margin-top: 36px;
  }

  .fo-contact h3 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .fo-contact p {
    color: ${palette.muted};
    font-size: 14px;
    margin-bottom: 20px;
  }

  .fo-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: ${palette.accent};
    color: #0A0E14;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 12px 24px;
    border-radius: 100px;
    text-decoration: none;
    transition: opacity .2s, transform .2s;
  }

  .fo-contact-btn:hover { opacity: .87; transform: translateY(-1px); }

  /* Footer */
  .fo-footer {
    text-align: center;
    padding-top: 28px;
    color: ${palette.faint};
    font-size: 12px;
  }

  @keyframes fo-fadeup {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// ── Icon helpers ──────────────────────────────────────────────────────────
const Ico = ({ e }) => <span role="img" aria-hidden="true">{e}</span>;

function SecIcon({ bg, children }) {
    return <div className="fo-icon" style={{ background: bg }}>{children}</div>;
}

function Section({ iconBg, icon, title, children, delay = 0 }) {
    return (
        <div className="fo-section" style={{ animationDelay: `${delay}s` }}>
            <div className="fo-section-head">
                <SecIcon bg={iconBg}><Ico e={icon} /></SecIcon>
                <h2>{title}</h2>
            </div>
            {children}
        </div>
    );
}

function SuccessBox({ children }) {
    return <div className="fo-box fo-box-success"><Ico e="✓" /><span>{children}</span></div>;
}

function WarnBox({ children }) {
    return <div className="fo-box fo-box-warning"><Ico e="⚠" /><span>{children}</span></div>;
}

function InfoCard({ icon, iconBg, title, desc }) {
    return (
        <div className="fo-card">
            <div className="fo-card-icon" style={{ background: iconBg }}>
                <Ico e={icon} />
            </div>
            <div>
                <strong>{title}</strong>
                <p>{desc}</p>
            </div>
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────
export default function FocusOSPrivacyPolicy() {
    const [year] = useState(new Date().getFullYear());

    return (
        <>
            <style>{css}</style>
            <div className="fo-pp">
                <div className="fo-rings-bg" />
                <div className="fo-glow" />

                <div className="fo-wrap">

                    {/* Hero */}
                    <header className="fo-hero">
                        <div className="fo-badge">🔐 &nbsp;Legal document</div>
                        <h1>Privacy <em>Policy</em></h1>
                        <p className="fo-hero-sub">
                            How <strong>FocusOS</strong> handles the permissions it asks for, and — just as
                            importantly — everything it doesn't do with them.
                        </p>
                        <div className="fo-meta">
                            <span>🧭 Developer: AlphaY</span>
                            <span>📅 Effective: July 16, {year}</span>
                            <span>📱 Free app · No ads · No account</span>
                        </div>
                    </header>

                    <div className="fo-divider" />

                    {/* Overview */}
                    <Section iconBg={palette.accentDim} icon="📄" title="Overview" delay={0.05}>
                        <p>
                            <strong style={{ color: palette.text }}>AlphaY</strong> built FocusOS as a free
                            Android launcher and focus tool — home screen, app blocking, a Pomodoro timer,
                            habits, a to-do list, and a screen-time dashboard, all in one app. This page
                            explains what FocusOS can access on your device, why, and what it does with it.
                        </p>
                        <p>By installing and using FocusOS, you agree to the practices described below.</p>
                        <SuccessBox>
                            FocusOS has no account, no sign-in, no ads, and no analytics or tracking SDKs.
                            Everything it stores is kept on your device — nothing is sent to us or anyone else.
                        </SuccessBox>
                    </Section>

                    {/* Permissions */}
                    <Section iconBg={palette.infoDim} icon="🔑" title="Permissions FocusOS uses" delay={0.10}>
                        <p>
                            Because FocusOS replaces your home screen and can block distracting apps, it needs
                            a few permissions that a typical app wouldn't. Each one is opt-in from Settings, and
                            FocusOS works with reduced functionality if you decline any of them.
                        </p>
                        <div className="fo-grid">
                            <InfoCard
                                icon="🧩" iconBg={palette.infoDim}
                                title="Accessibility service"
                                desc="Used only to detect which app is currently open, so a blocked app can be interrupted during a Focus session. It never reads screen content, text, or keystrokes."
                            />
                            <InfoCard
                                icon="📊" iconBg={palette.accentDim}
                                title="Usage access"
                                desc="Powers the Wellbeing screen (time spent per app, unlocks, pickups). Read from your device and never leaves it."
                            />
                            <InfoCard
                                icon="🏠" iconBg="#1A1030"
                                title="Default launcher"
                                desc="Lets FocusOS act as your home screen and app drawer. You can switch back to any other launcher at any time."
                            />
                            <InfoCard
                                icon="🔔" iconBg={palette.warningDim}
                                title="Notifications"
                                desc="Local reminders only — Pomodoro timers ending, focus sessions completing. FocusOS never sends push notifications from a server."
                            />
                            <InfoCard
                                icon="🖼️" iconBg={palette.successDim}
                                title="Photos"
                                desc="Only used if you choose a custom wallpaper or app icon from your gallery. Nothing is uploaded anywhere."
                            />
                            <InfoCard
                                icon="🔒" iconBg="#1A100A"
                                title="Set wallpaper"
                                desc="Applies your chosen wallpaper to your home and/or lock screen, only when you turn that on."
                            />
                        </div>
                    </Section>

                    {/* Data Collected */}
                    <Section iconBg={palette.successDim} icon="🗂️" title="Information we collect" delay={0.15}>
                        <p>
                            <strong style={{ color: palette.text }}>We do not collect personally identifiable information.</strong>{" "}
                            FocusOS does not require an account, name, email, or any profile data.
                        </p>
                        <p>
                            Settings, focus sessions, habits, to-dos, and screen-time stats are stored{" "}
                            <strong style={{ color: palette.text }}>locally on your device only</strong>, via
                            Android SharedPreferences. None of it is transmitted to any server, because FocusOS
                            doesn't have one.
                        </p>
                        <SuccessBox>
                            Uninstalling FocusOS permanently deletes all of this data from your device.
                        </SuccessBox>
                    </Section>

                    {/* Third Parties */}
                    <Section iconBg={palette.warningDim} icon="🔌" title="Third-party services" delay={0.20}>
                        <p>
                            FocusOS keeps its list of outside dependencies short on purpose — there is no
                            advertising network and no analytics SDK anywhere in the app.
                        </p>
                        <div className="fo-grid">
                            <InfoCard
                                icon="▶️" iconBg="#0A1A10"
                                title="Google Play Services"
                                desc="Used by Android itself for app distribution and updates. FocusOS doesn't add any tracking on top of it."
                            />
                            <InfoCard
                                icon="💾" iconBg={palette.successDim}
                                title="SharedPreferences"
                                desc="Local on-device storage only. Your data never leaves your phone."
                            />
                        </div>
                        <WarnBox>
                            FocusOS does not use Google AdMob, Firebase Analytics, Crashlytics, or any similar
                            service. If that ever changes, this policy will be updated first.
                        </WarnBox>
                    </Section>

                    {/* Data Retention */}
                    <Section iconBg="#0A1525" icon="🕓" title="Data retention" delay={0.25}>
                        <p>
                            Everything FocusOS stores — settings, focus history, habits, to-dos, wellbeing
                            stats — stays on your device until you clear the app's storage or uninstall it,
                            at which point Android deletes it automatically.
                        </p>
                        <p>
                            We do not maintain any server-side database of your data, because none of it is
                            ever sent to us in the first place.
                        </p>
                    </Section>

                    {/* Children */}
                    <Section iconBg={palette.dangerDim} icon="🧒" title="Children's privacy" delay={0.30}>
                        <p>
                            FocusOS is a general-audience productivity tool and is not directed at children. We
                            do not knowingly collect personal information from children under 13 (or under 16
                            in the EU/EEA) — in practice, this is straightforward, since FocusOS doesn't collect
                            personal information from anyone, of any age.
                        </p>
                        <p>
                            If you believe a child has been affected in a way this policy doesn't account for,
                            please contact us at{" "}
                            <a href="mailto:alphay28071994@gmail.com">alphay28071994@gmail.com</a> and we'll
                            look into it promptly.
                        </p>
                    </Section>

                    {/* Security */}
                    <Section iconBg={palette.successDim} icon="🛡️" title="Security" delay={0.35}>
                        <p>
                            Since FocusOS doesn't transmit your personal data anywhere, your main security
                            consideration is your device itself. We recommend keeping Android and FocusOS
                            updated, and being deliberate about which accessibility services you enable —
                            including this one.
                        </p>
                    </Section>

                    {/* Changes */}
                    <Section iconBg={palette.warningDim} icon="🔄" title="Changes to this policy" delay={0.40}>
                        <p>
                            We may update this privacy policy from time to time. Changes will be posted on this
                            page with an updated effective date at the top.
                        </p>
                        <p>
                            We encourage you to review this policy periodically. Continued use of FocusOS after
                            changes are posted constitutes acceptance of the updated policy.
                        </p>
                    </Section>

                    {/* Consent */}
                    <Section iconBg={palette.successDim} icon="✅" title="Your consent" delay={0.45}>
                        <p>
                            By installing and using FocusOS, you consent to this privacy policy. If you do not
                            agree, please discontinue use of the app, turn off any permissions you granted it in
                            Android Settings, and uninstall it.
                        </p>
                    </Section>

                    {/* Contact */}
                    <div className="fo-contact">
                        <h3>Questions or concerns?</h3>
                        <p>
                            If anything here isn't clear, or you want to know more about how a specific
                            permission is used, reach out any time.
                        </p>
                        <a href="mailto:alphay28071994@gmail.com" className="fo-contact-btn">
                            ✉️ &nbsp;alphay28071994@gmail.com
                        </a>
                    </div>

                    {/* Footer */}
                    <div className="fo-divider" />
                    <footer className="fo-footer">
                        <p>© {year} <strong style={{ color: palette.muted }}>AlphaY</strong> · FocusOS · All rights reserved.</p>
                        <p style={{ marginTop: 5 }}>This app is not affiliated with or endorsed by Google LLC.</p>
                    </footer>

                </div>
            </div>
        </>
    );
}